import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Button from './Button';
import Icon from '../AppIcon';
import RegionSelector from './RegionSelector';
import { appendTrackingParams } from '../../utils/tracking';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [ctaPressed, setCtaPressed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pathname = location?.pathname ?? '';

  const isAccountingMarketing =
    pathname === '/accounting' || pathname.startsWith('/accounting/');

  const defaultNavigationItems = [
    { name: 'Home', path: '/', icon: 'Home' },
    { name: 'Charities', path: '/charity', icon: 'Heart' },
    { name: 'Pricing', path: '/subscription', icon: 'CreditCard' },
    { name: 'Neevii sheets sidebar', path: '/sheets', icon: 'Table2' },
    { name: 'New Features', path: '/updates', icon: 'Sparkles' },
    { name: 'Tutorials', path: '/tutorials', icon: 'BookOpen' },
    { name: 'FAQ', path: '/faq', icon: 'HelpCircle' },
  ];

  const navigationItems = defaultNavigationItems;

  const showMainAppAccountCtas = true;

  const isActivePath = (path) => location?.pathname === path;

  const appUrl = (import.meta.env.VITE_APP_URL || 'https://app.neevii.com').replace(/\/$/, '');

  const getAppUrl = () => {
    const region = typeof window !== 'undefined' ? localStorage.getItem('neevii-website-region') : null;
    if (region === 'intl') return `${appUrl}/login?country=XX`;
    return appUrl;
  };

  const handleNavigation = (path, isExternal=false) => {
    if (isExternal) {
      window.location.href = appendTrackingParams(path);
    } else {
      navigate(path);
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[200] bg-[#c1c9d8] backdrop-blur-md transition-all duration-300 shadow-[0_6px_18px_rgba(9,24,49,0.16)]"
    >
      <div className="relative w-full">
        {/* Thin brand strip under header */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#7a5f11]" aria-hidden="true" />
        {/* Top status banner */}
        <div className="w-full border-b border-border bg-card/40">
          <div className="mx-auto max-w-7xl px-3 sm:px-4">
            <div
              className="flex flex-wrap items-center justify-center gap-3 py-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground sm:text-xs md:text-sm"
              aria-live="polite"
            >
              <p className="m-0 flex items-center justify-center gap-2">
                <span aria-hidden="true">🇬🇧 UK live</span>
                <span className="text-muted-foreground/60" aria-hidden="true">
                  •
                </span>
                <span aria-hidden="true">🇺🇸 U.S. waitlist open</span>
                <span className="text-muted-foreground/60" aria-hidden="true">
                  •
                </span>
                <span aria-hidden="true">🌍 International</span>
              </p>
              <span className="sr-only">
                United Kingdom Early Access is live. United States waitlist is open. International users welcome.
              </span>
              {!isAccountingMarketing && (
                <Link
                  to="/"
                  className="inline-flex shrink-0 items-center opacity-90 transition-opacity hover:opacity-100"
                  aria-label="Neevii — product hub home"
                >
                  <img
                    src="/assets/images/Neevii_Banner_logo.png"
                    alt=""
                    className="h-[1.47rem] w-auto sm:h-[1.764rem]"
                  />
                </Link>
              )}
            </div>
          </div>
        </div>
			<div className="relative flex h-16 items-center px-6 lg:px-8">
			  {/* Logo is rendered in the Hero section (landing page body); flex-1 balances the right cluster so centered nav does not overlap Region/CTAs */}
			  <div className="min-w-0 flex-1" aria-hidden="true" />

          {/* Desktop Navigation — shrink-0 + flex-1 gutters avoids absolute center overlap at 1920px */}
          <nav className="hidden shrink-0 lg:flex lg:items-center lg:gap-x-3 xl:gap-x-4 2xl:gap-x-6">
            {navigationItems?.map((item) => (
              <button
                key={`${item.path}-${item.name}`}
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center space-x-2 rounded-lg px-2.5 py-2 text-xs font-medium transition-all duration-200 xl:px-3.5 xl:text-sm ${
                  isActivePath(item?.path)
                    ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span className="whitespace-nowrap">{item?.name}</span>
              </button>
            ))}
          </nav>

          {/* Desktop Region Selector + CTA; mobile menu on small screens */}
          <div className="flex min-w-0 flex-1 items-center justify-end gap-3 sm:gap-4">
            <div className="hidden items-center gap-4 lg:flex">
              {showMainAppAccountCtas && (
                <>
                  <RegionSelector />
                  <div
                    className={`flex items-center space-x-0 ${ctaPressed ? 'ring-2 ring-[#cc9500] ring-offset-2 ring-offset-background rounded-md' : ''}`}
                    onMouseDown={() => setCtaPressed(true)}
                    onMouseUp={() => setCtaPressed(false)}
                    onMouseLeave={() => setCtaPressed(false)}
                    onTouchStart={() => setCtaPressed(true)}
                    onTouchEnd={() => setCtaPressed(false)}
                    onTouchCancel={() => setCtaPressed(false)}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleNavigation(getAppUrl(), true)}
                      className={`border border-brand text-brand bg-transparent hover:bg-transparent hover:text-brand font-semibold uppercase tracking-wide ${ctaPressed ? 'ring-2 ring-brand ring-offset-2 ring-offset-background' : ''}`}
                      aria-label="Sign In"
                    >
                      Sign In
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className={`bg-brand text-slate-900 hover:bg-brand/90 shadow-lg font-semibold uppercase tracking-wide ${ctaPressed ? 'ring-2 ring-brand ring-offset-2 ring-offset-background' : ''}`}
                      onClick={() => handleNavigation(getAppUrl(), true)}
                      aria-label="See How It Works"
                    >
                      See How It Works
                    </Button>
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden transition-all duration-300 ease-brand ${
            isMobileMenuOpen 
              ? 'max-h-96 opacity-100' :'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <div className="px-6 py-4 bg-background/85 backdrop-blur-md border-t border-border">
            <nav className="space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={`${item.path}-${item.name}`}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span className="font-medium">{item?.name}</span>
                </button>
              ))}
            </nav>

            {showMainAppAccountCtas && (
              <>
                {/* Mobile Region Selector */}
                <div className="mt-4">
                  <RegionSelector />
                </div>

                <div className="mt-6 space-y-3 border-t border-border pt-4">
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={() => handleNavigation(getAppUrl(), true)}
                    className="justify-start text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="LogIn" size={18} className="mr-3" />
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    fullWidth
                    className="bg-brand font-semibold uppercase tracking-wide text-slate-900 shadow-lg hover:bg-brand/90"
                    onClick={() => handleNavigation(getAppUrl(), true)}
                  >
                    <Icon name="Zap" size={18} className="mr-2" />
                    See How It Works
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </header>
  );
};

export default Header;